import React, { useEffect } from 'react';
import { Box, Button, Divider, Fade, Grid, IconButton, Modal, TextField, Typography } from '@mui/material';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import CloseIcon from '@mui/icons-material/Close';
import { ChargeItemValidationSchema } from '../../../validations/ChargeValidation';
import { yupResolver } from '@hookform/resolvers/yup';
import pricingEngineForDocuments from '../../../utils/pricingEngineForDocuments';
import NumberOrPercentageInput from '../../../components/NumberOrPercentageInput';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '500px',
  bgcolor: 'background.paper',
  borderRadius: '15px',
  boxShadow: 24,
};

export default function InvoiceChargePopup({
  watch,
  open,
  onClose,
  chargeEditing,
  replaceCharge,
  setChargeEditing,
  chargeAppend,
  openPopup,
}) {
  useEffect(() => {
    if (chargeEditing) {
      resetPopupForm({
        charges: watch('charges'),
      });
      openPopup();
    }
  }, [chargeEditing]);

  const {
    handleSubmit: handlePopupSubmit,
    control: popupControl,
    reset: resetPopupForm,
    formState: { errors: popupErrors },
  } = useForm({
    defaultValues: {
      charges: [
        {
          type: 'percentage',
          amount: 0,
          value: 0,
          reason: '',
        },
      ],
    },
    resolver: yupResolver(ChargeItemValidationSchema),
  });

  useEffect(() => {
    if (open) return;
    resetPopupForm({
      charges: [
        {
          type: 'percentage',
          amount: 0,
          value: 0,
          reason: '',
        },
      ],
    });
    setChargeEditing(false);
  }, [open]);

  const { fields: chargeFields } = useFieldArray({
    control: popupControl,
    name: 'charges',
  });

  const handleSubmitForm = (data) => {
    const { charges } = pricingEngineForDocuments(watch('item_details'), data.charges);
    if (chargeEditing) {
      replaceCharge(charges);
      setChargeEditing(false);
    } else {
      chargeAppend(charges);
    }
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          onClose;
        }
      }}
      className={'backdrop-model'}
    >
      <Fade in={open}>
        <Box sx={style}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: 2,
              py: 2,
              borderBottom: '1px solid #e0e0e0',
            }}
          >
            <Typography variant="h6">Add Charge</Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider />
          <Box sx={{ p: 2 }}>
            {chargeFields.map((charge, chargeIndex) => (
              <Grid key={charge.id} container spacing={2} rowspacing={2}>
                <Grid item md={12}>
                  <NumberOrPercentageInput
                    value={`charges.${chargeIndex}.value`}
                    type={`charges.${chargeIndex}.type`}
                    id={'charge-value'}
                    label={'Charges Value'}
                    control={popupControl}
                    error={!!popupErrors.charges?.[chargeIndex]?.value}
                    helperText={popupErrors.charges?.[chargeIndex]?.value?.message}
                  />
                </Grid>
                <Grid item md={12}>
                  <Controller
                    name={`charges.${chargeIndex}.reason`}
                    control={popupControl}
                    render={({ field }) => (
                      <TextField {...field} id="charge-reason" fullWidth label="Charge Reason" variant="outlined" />
                    )}
                  />
                </Grid>
              </Grid>
            ))}
          </Box>
          <Divider />
          <Box
            sx={{
              px: 2,
              py: 1,
              borderTop: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
            }}
          >
            <Button
              id="add-charge"
              type="button"
              variant="contained"
              color="primary"
              onClick={handlePopupSubmit(handleSubmitForm)}
            >
              Add Charge
            </Button>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
}
